import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import LeftArrow from "../../../../images/icons/blue-left-arrow.svg";
import TimeIccon from "../../../../images/icons/clock.svg";
import DollarIccon from "../../../../images/icons/billable.svg";
import CloseDollarIccon from "../../../../images/icons/non-billable.svg";
import PurseMmoney from "../../../../images/icons/purses-money.svg";
import NotepadIcon from "../../../../images/icons/notepad.svg";
import DollarReverse from "../../../../images/icons/dollar-reverse.svg";
import profileImage from "../../../../images/user/Default_pfp.png";
import { setSprintData, setSprintView } from "../../../../features/projects/projectSlice";
import { useGetActiveTeams } from "../../../../components/TimeTracker/TimeTrackerOverview/useTimeTracker.jsx";
import SprintViewModal from "./modals/SprintViewModal";
import MvpProjectAlertsModal from "./MvpProjectAlertsModal";
import "./MvpProjectDetails.css";
import "./MvpSprintDetails.css";

function formatDuration(sec) {
  const second = Math.max(0, Math.floor(Number(sec) || 0));
  const hours = Math.floor(second / 3600);
  const minutes = Math.floor((second % 3600) / 60);
  const seconds = second % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function money(amount, currency) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
    maximumFractionDigits: 2,
  }).format(Number(amount) || 0);
}

function profileSrc(image) {
  if (!image || image === "null") return null;
  const file = String(image).split("/").pop().replace(/[^a-zA-Z0-9._-]/g, "");
  return file ? `/uploads/lowProfileImage/${file}` : null;
}

function roiLabel(budget, cost) {
  if (!cost && !budget) return "0%";
  if (!cost) return "N/A";
  const value = ((budget - cost) / cost) * 100;
  return `${Number.isInteger(value) ? value : value.toFixed(2)}%`;
}

function HoverText({ text }) {
  const clipRef = useRef(null);
  const [truncated, setTruncated] = useState(false);
  const value = text || "—";

  useEffect(() => {
    const el = clipRef.current;
    if (!el) return undefined;
    const measure = () => setTruncated(el.scrollWidth > el.clientWidth + 1);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [value]);

  return (
    <span className="mvp-hover-text">
      <span ref={clipRef} className="mvp-hover-text-clip">{value}</span>
      {truncated && <span className="mvp-hover-text-full">{value}</span>}
    </span>
  );
}

function SortMark() {
  return (
    <span className="mvp-sprint-sort" aria-hidden="true">
      <svg xmlns="http://www.w3.org/2000/svg" width="7" height="5" viewBox="0 0 7 5" fill="none">
        <path d="M0.424316 3.64275L3.21843 0.848633L6.01255 3.64275" strokeWidth="1.2" stroke="#405BFF" />
      </svg>
      <svg xmlns="http://www.w3.org/2000/svg" width="7" height="5" viewBox="0 0 7 5" fill="none">
        <path d="M6.0127 0.424632L3.21858 3.21875L0.42446 0.424632" strokeWidth="1.2" stroke="#405BFF" />
      </svg>
    </span>
  );
}

const MvpSprintDetails = ({ clientId, encryptedId, project, sprintId, isLoading, currency }) => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const { data: teams = [] } = useGetActiveTeams(token);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [sort, setSort] = useState({ key: "date", dir: "desc" });
  const logCardRef = useRef(null);
  const logScrollRef = useRef(null);

  const sprint = (project?.sprints || []).find((item) => String(item._id) === String(sprintId));
  const teamById = useMemo(() => {
    const map = {};
    teams.forEach((team) => { map[String(team._id)] = team; });
    return map;
  }, [teams]);

  const rows = useMemo(() => {
    const entries = (project?.timers || []).filter((timer) => {
      const id = timer.sprint?._id || timer.sprint;
      return id && String(id) === String(sprintId);
    }).map((timer) => {
      const userId = String(timer.user?._id || timer.user || "");
      const team = teamById[userId];
      const name = team
        ? `${team.first_name || team.company_first_name || ""} ${team.last_name || team.company_last_name || ""}`.trim()
        : "User";
      return {
        id: timer._id,
        name: name || "User",
        image: team?.profile_image,
        description: timer.task || "—",
        duration: Number(timer.duration) || 0,
        date: timer.date || timer.startTime || timer.created_at,
        billable: timer.billable !== false,
        cost: Number(timer.totalCost) || 0,
      };
    });
    const dir = sort.dir === "asc" ? 1 : -1;
    return entries.sort((a, b) => {
      const left = a[sort.key];
      const right = b[sort.key];
      if (typeof left === "number" && typeof right === "number") return (left - right) * dir;
      return String(left || "").localeCompare(String(right || "")) * dir;
    });
  }, [project, sprintId, teamById, sort]);

  const totals = rows.reduce((sum, row) => {
    sum.hours += row.duration;
    sum.billable += row.billable ? row.duration : 0;
    sum.nonBillable += row.billable ? 0 : row.duration;
    sum.cost += row.cost;
    return sum;
  }, { hours: 0, billable: 0, nonBillable: 0, cost: 0 });
  const budget = Number(sprint?.budget) || 0;

  useEffect(() => {
    const tableEl = logScrollRef.current;
    const card = logCardRef.current;
    if (!tableEl || !card) return undefined;

    let atEnd = false;
    const updatePadding = () => {
      if (window.innerWidth > 991) {
        atEnd = false;
        card.classList.remove("mvp-hscroll-end");
        return;
      }
      const maxScroll = Math.max(0, tableEl.scrollWidth - tableEl.clientWidth);
      if (maxScroll <= 2) {
        atEnd = false;
        card.classList.remove("mvp-hscroll-end");
        return;
      }
      if (!atEnd && tableEl.scrollLeft >= maxScroll - 4) {
        atEnd = true;
        card.classList.add("mvp-hscroll-end");
      } else if (atEnd && tableEl.scrollLeft < maxScroll - 24) {
        atEnd = false;
        card.classList.remove("mvp-hscroll-end");
      }
    };

    tableEl.addEventListener("scroll", updatePadding, { passive: true });
    window.addEventListener("resize", updatePadding);
    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updatePadding) : null;
    resizeObserver?.observe(tableEl);
    updatePadding();

    return () => {
      tableEl.removeEventListener("scroll", updatePadding);
      window.removeEventListener("resize", updatePadding);
      resizeObserver?.disconnect();
      card.classList.remove("mvp-hscroll-end");
    };
  }, [rows.length, isLoading]);

  const toggleSort = (key) => {
    setSort((current) => ({
      key,
      dir: current.key === key && current.dir === "asc" ? "desc" : "asc",
    }));
  };

  const openEdit = () => {
    if (!sprint) return;
    dispatch(setSprintData({
      ...sprint,
      project_name: project?.project_name,
      clientId: project?.client_name?._id || clientId,
    }));
    dispatch(setSprintView(true));
    setEditOpen(true);
  };

  const backTo = `/client/projects/details/${project?._id || ""}/${encodeURIComponent(encryptedId)}`;

  return (
    <div className="client-details-overview-outer mvp-pd mvp-sd">
      <div className="text-arrow-outer back-button-with-comment">
        <div className="text-arrow-inner">
          <Link to={backTo}>
            <div className="text-arrow">
              <div className="left-arrow">
                <img src={LeftArrow} alt="" />
              </div>
              <div className="top-text">Back to Project</div>
            </div>
          </Link>
        </div>
      </div>
      <div className="client-with-invitation">
        <button type="button" className="btn-blue-sec" onClick={openEdit}>
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
            <path d="M21.4239 5.38009L16.4837 0.41075C16.4129 0.339465 16.319 0.30031 16.2187 0.300313C16.1184 0.300316 16.0251 0.339476 15.9542 0.410765L13.0274 3.35519L13.0239 3.36574L12.9656 3.41745L1.66111 14.79C1.60871 14.8427 1.57328 14.909 1.55881 14.9813L0.307401 21.2109C0.282453 21.3349 0.320883 21.4619 0.409715 21.5518C0.480581 21.6225 0.574402 21.6617 0.674709 21.6617C0.699661 21.6617 0.724114 21.6592 0.74707 21.6547L6.94064 20.3949C7.0135 20.3803 7.07887 20.3447 7.13077 20.2925L21.4239 5.91273C21.4948 5.84195 21.5332 5.74706 21.5332 5.64616C21.5332 5.54525 21.4942 5.45087 21.4239 5.38009ZM6.68061 19.6795L1.15227 20.8042L2.26946 15.2423L3.90127 13.6007L8.31243 18.0384L6.68061 19.6795ZM8.8414 17.5057L4.43024 13.0685L13.2376 4.20812L17.6482 8.64534L8.8409 17.5057L8.8414 17.5057ZM18.1782 8.11268L13.7675 3.67546L16.2188 1.20947L20.6299 5.64669L18.1782 8.11268Z" fill="white" strokeWidth="0.6" stroke="white" />
          </svg>
          Edit Sprint
        </button>
      </div>

      <div className="client-professional-details-outer">
      <div className="mvp-pd-summary">
        <div className="client-detail-row-one">
          <div className="client-detail-row-one-left">
            <div className="client-status">
              <div className="client-status-text client-status-text-title">
                {isLoading ? <span className="mvp-sd-skel mvp-sd-skel-title" /> : (sprint?.sprint_name || "Sprint")}
              </div>
            </div>
          </div>
          <div className="client-detail-row-one-right">
            <button type="button" className="mvp-pd-alerts" onClick={() => setAlertsOpen(true)}>
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M9.4 0.76V2.28" stroke="#A74E4E" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M9.4 22.75C8.11 22.75 7.07 21.71 7.07 20.42V19.05H11.73V20.42C11.73 21.71 10.69 22.75 9.4 22.75Z" stroke="#A74E4E" strokeWidth="1.5" />
                <path d="M15.72 12.17V15.23H3.08V8.96C3.08 5.47 5.91 2.64 9.4 2.64C12.89 2.64 15.72 5.47 15.72 8.96V12.17Z" stroke="#A74E4E" strokeWidth="1.5" />
                <path d="M0.75 17.14C0.75 18.2 1.6 19.05 2.66 19.05H16.14C17.2 19.05 18.05 18.2 18.05 17.14C18.05 16.09 17.2 15.23 16.14 15.23H2.66C1.6 15.23 0.75 16.09 0.75 17.14Z" stroke="#A74E4E" strokeWidth="1.5" />
              </svg>
              View Sprint Alerts
            </button>
          </div>
        </div>
        <div className="client-activities-outer">
        <div className="client-activity-columns">
          <div className="activity-column">
            <div className="activity-with-image"><div className="activity-name">Total Hours to Date</div><img src={TimeIccon} alt="" /></div>
            <div className="activity-count">{isLoading ? <span className="mvp-sd-skel mvp-sd-skel-value" /> : formatDuration(totals.hours)}</div>
          </div>
          <div className="activity-column">
            <div className="activity-with-image"><div className="activity-name">Total Budget</div><img src={PurseMmoney} alt="" /></div>
            <div className="activity-count">{isLoading ? <span className="mvp-sd-skel mvp-sd-skel-value is-money" /> : money(budget, currency)}</div>
          </div>
          <div className="activity-column">
            <div className="activity-with-image"><div className="activity-name">Total Billable Hours</div><img src={DollarIccon} alt="" /></div>
            <div className="activity-count">{isLoading ? <span className="mvp-sd-skel mvp-sd-skel-value" /> : formatDuration(totals.billable)}</div>
          </div>
          <div className="activity-column">
            <div className="activity-with-image"><div className="activity-name">Total Non-Billable Hours</div><img src={CloseDollarIccon} alt="" /></div>
            <div className="activity-count">{isLoading ? <span className="mvp-sd-skel mvp-sd-skel-value" /> : formatDuration(totals.nonBillable)}</div>
          </div>
          <div className="activity-column">
            <div className="activity-with-image"><div className="activity-name">Cost to Date</div><img src={NotepadIcon} alt="" /></div>
            <div className="activity-count">{isLoading ? <span className="mvp-sd-skel mvp-sd-skel-value is-money" /> : money(totals.cost, currency)}</div>
          </div>
          <div className="activity-column">
            <div className="activity-with-image"><div className="activity-name">ROI to Date</div><img src={DollarReverse} alt="" /></div>
            <div className="activity-count">{isLoading ? <span className="mvp-sd-skel mvp-sd-skel-value is-short" /> : roiLabel(budget, totals.cost)}</div>
          </div>
        </div>
        </div>
      </div>

      <section className="member-main-outter table-top-project-client mvp-pd-team-card mvp-sd-log" ref={logCardRef}>
        <div className="table-head-outer">
          <div className="table-head">Logged Time</div>
        </div>
        <div className="time-table" ref={logScrollRef}>
          <table>
            <colgroup>
              <col className="mvp-sd-col-user" />
              <col className="mvp-sd-col-desc" />
              <col className="mvp-sd-col-duration" />
              <col className="mvp-sd-col-date" />
              <col className="mvp-sd-col-bill" />
            </colgroup>
            <thead className="head-tb-of-team-member">
              <tr>
                {["name", "description", "duration", "date", "billable"].map((key) => (
                  <th key={key}>
                    <button type="button" className="mvp-sd-sort" onClick={() => toggleSort(key)}>
                      {{ name: "User", description: "Description", duration: "Duration", date: "Date", billable: "Billable" }[key]}
                      <SortMark />
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="body-tb-of-team-member">
              {isLoading &&
                Array.from({ length: 6 }).map((_, index) => (
                  <tr key={`sprint-skeleton-${index}`}>
                    <td>
                      <span className="mvp-sd-user">
                        <span className="mvp-sd-skel mvp-sd-skel-avatar" />
                        <span className="mvp-sd-skel mvp-sd-skel-name" />
                      </span>
                    </td>
                    <td><span className="mvp-sd-skel mvp-sd-skel-text" /></td>
                    <td><span className="mvp-sd-skel mvp-sd-skel-time" /></td>
                    <td><span className="mvp-sd-skel mvp-sd-skel-date" /></td>
                    <td><span className="mvp-sd-skel mvp-sd-skel-pill" /></td>
                  </tr>
                ))}
              {!isLoading && rows.length === 0 ? (
                <tr><td colSpan="5">No time logged for this sprint.</td></tr>
              ) : !isLoading && rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <span className="mvp-sd-user">
                      <img
                        className="member-image"
                        src={profileSrc(row.image) || profileImage}
                        alt=""
                        onError={(event) => {
                          event.currentTarget.onerror = null;
                          event.currentTarget.src = profileImage;
                        }}
                      />
                      <HoverText text={row.name} />
                    </span>
                  </td>
                  <td><HoverText text={row.description} /></td>
                  <td>{formatDuration(row.duration)}</td>
                  <td>{row.date ? moment(row.date).format("MMMM DD, YYYY") : "—"}</td>
                  <td>
                    <span className={row.billable ? "mvp-sd-yes" : "mvp-sd-no"}>
                      {row.billable ? "Yes" : "No"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      </div>

      <SprintViewModal preferredCurrency={currency} openInEdit={editOpen} />
      <MvpProjectAlertsModal
        isOpen={alertsOpen}
        onClose={() => setAlertsOpen(false)}
        recordId={sprintId}
        clientId={project?.client_name?._id || clientId}
        thresholdFor="sprints"
      />
    </div>
  );
};

export default MvpSprintDetails;
